import {Alert, Spin, message} from "antd";
import {useState, useEffect} from "react";
import {BookingDataProvider} from "../../../../../../../container/booking/booking-context";
import {TCustomerPaidBundleApiResponse} from "../../../../../../../react-query-hooks/customer";
import {
  useCreateBundleBooking,
  useGetPublicCoachProfile,
} from "../../../../../../../react-query-hooks/public-profile";
import {useBookingTimeSlots} from "../../../../../../../features/booking-availability";
import {StyledModalContentWrapper} from "../../../../call-card/call-card.style";
import {CalendarContent} from "./calendar-content";
import {SuccessContent} from "./success-content";

const CalendarModalContent = ({bundle}: {bundle: TCustomerPaidBundleApiResponse}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);

  const updateStep = (step: number) => {
    if (step > 2) {
      return;
    }
    setCurrentStep(step);
  };
  // The bundle row carries the offering outright (id, duration, call type,
  // booking questions), so there is nothing left to fetch about the service.
  // What it does not carry is the coach's numeric user id, and the availability
  // endpoints key off it - hence this one lookup by username.
  const coachProfileQuery = useGetPublicCoachProfile(bundle.coachUsername);
  const timeSlots = useBookingTimeSlots();
  const createBundleBookingQueryMutation = useCreateBundleBooking();

  // The old v1 mutation (useGetPublicProfileBookingTimeslots) surfaced fetch
  // failures as a global toast via its onError handler. useBookingTimeSlots is a
  // plain query hook with no such side effect, so replicate the toast here to
  // keep this call site's error behavior unchanged.
  useEffect(() => {
    if (timeSlots.error) {
      message.error(timeSlots.error.message);
    }
  }, [timeSlots.error]);

  // isInitialLoading, not isLoading: the profile query is disabled when the
  // bundle has no coach username, and a disabled query reports isLoading
  // forever, which would leave the modal stuck behind a spinner.
  if (coachProfileQuery.isInitialLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "40vh",
        }}
      >
        <Spin />
      </div>
    );
  }

  const mentorId = coachProfileQuery.data?.userId ?? 0;

  if (coachProfileQuery.isError || !bundle.service || !mentorId) {
    return (
      <StyledModalContentWrapper>
        <Alert
          message="Error"
          description={
            coachProfileQuery.error?.message ??
            "We couldn't load this coach's booking details. Please try again."
          }
          type="error"
          showIcon
        />
      </StyledModalContentWrapper>
    );
  }

  const service = bundle.service;

  function handleView() {
    let content;
    switch (currentStep) {
      case 2:
        content = <SuccessContent />;
        break;
      default:
      case 1:
        content = (
          <CalendarContent
            timeSlots={timeSlots}
            mentorId={mentorId}
            service={service}
            bundle={bundle}
            bundleBookingMutation={createBundleBookingQueryMutation}
            updateStep={updateStep}
          />
        );
    }
    return content;
  }
  return (
    <StyledModalContentWrapper>
      <BookingDataProvider>{handleView()}</BookingDataProvider>
    </StyledModalContentWrapper>
  );
};

export {CalendarModalContent};
